import { Box, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ChangeEvent, Dispatch, FC, SetStateAction, useMemo } from 'react';

import { _getAdminUserTags } from '@/api/admin/queries';
import { AdminDashboardUserStatus } from '@/api/admin/types';
import { Input, Select, Tooltip } from '@/design-system';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { Permission, useCan } from '@/shared/modules/permissions';

import { ResetFiltersButton } from '../../components/reset-filters-button';
import { usersTableColumns } from '../data';

const TAGS_DENIED_TOOLTIP = 'You do not have permission to filter users by tag (UserAdminManage).';

const statusOptions = [
  {
    value: AdminDashboardUserStatus.Pending,
    label: 'Pending',
  },
  {
    value: AdminDashboardUserStatus.SignedUp,
    label: 'Signed Up',
  },
  {
    value: AdminDashboardUserStatus.WaitingForApproval,
    label: 'Waiting for Approval',
  },
  {
    value: AdminDashboardUserStatus.VerifiedBasic,
    label: 'Verified Basic',
  },
  {
    value: AdminDashboardUserStatus.VerifiedEntity,
    label: 'Verified Entity',
  },
  {
    value: AdminDashboardUserStatus.Blocked,
    label: 'Blocked',
  },
];

const booleanOptions = [
  {
    value: 'yes',
    label: 'Yes',
  },
  {
    value: 'no',
    label: 'No',
  },
];

const columnsGroups = {
  optional: { label: 'Optional columns' },
  required: { label: 'Always visible' },
};
const requiredColumns = ['name', 'actions'];
const columnsOptions = usersTableColumns.map((column) => {
  const isRequiredColumn = requiredColumns.includes(column.key);
  return {
    value: column.key,
    label: column.header,
    group: isRequiredColumn ? 'required' : 'optional',
    disabled: isRequiredColumn,
  };
});

export interface UserTabFiltersValue {
  search: string;
  status: AdminDashboardUserStatus[];
  entityAffiliation: boolean[];
  gammaTeam: boolean[];
  tags: string[];
  columns: string[];
}

interface Props {
  value: UserTabFiltersValue;
  setValue: Dispatch<SetStateAction<UserTabFiltersValue>>;
}

const booleanToSelectValue = (v: boolean) => (v ? 'yes' : 'no');
const selectToBooleanValue = (v: string) => v === 'yes';

export const userTabFiltersDefaultValue: Omit<UserTabFiltersValue, 'columns'> = {
  search: '',
  status: [],
  entityAffiliation: [],
  gammaTeam: [],
  tags: [],
};

export const UserTabFilters: FC<Props> = ({ value, setValue }) => {
  const { search, status, entityAffiliation, gammaTeam, tags, columns } = value;

  const isMobile = useIsMobile();

  const canViewTags = useCan(Permission.UserAdminManage);
  const { data: availableTags } = useQuery({ ..._getAdminUserTags(), enabled: canViewTags });
  const tagOptions = useMemo(
    () => (availableTags ?? []).map((tag) => ({ value: tag.name, label: tag.name })),
    [availableTags],
  );

  const selectEntityAffiliationValue = useMemo(
    () => entityAffiliation.map(booleanToSelectValue),
    [entityAffiliation],
  );
  const selectGammaTeamValue = useMemo(() => gammaTeam.map(booleanToSelectValue), [gammaTeam]);

  const {
    onSearchChange,
    onStatusChange,
    onEntityAffiliationChange,
    onGammaTeamChange,
    onTagsChange,
    onColumnsChange,
    resetFilters,
  } = useMemo(
    () => ({
      onSearchChange: (e: ChangeEvent<HTMLInputElement>) =>
        setValue((prev) => ({ ...prev, search: e.target.value })),
      onStatusChange: (v: string[]) =>
        setValue((prev) => ({ ...prev, status: v as AdminDashboardUserStatus[] })),
      onEntityAffiliationChange: (v: string[]) =>
        setValue((prev) => ({ ...prev, entityAffiliation: v.map(selectToBooleanValue) })),
      onGammaTeamChange: (v: string[]) =>
        setValue((prev) => ({ ...prev, gammaTeam: v.map(selectToBooleanValue) })),
      onTagsChange: (v: string[]) => setValue((prev) => ({ ...prev, tags: v })),
      onColumnsChange: (v: string[]) =>
        setValue((prev) => ({
          ...prev,
          columns: [...new Set(['name', 'actions', ...v])],
        })),
      resetFilters: () => setValue((prev) => ({ ...prev, ...userTabFiltersDefaultValue })),
    }),
    [setValue],
  );

  const showResetFilter = useMemo(
    () =>
      !!value.search ||
      value.status.length > 0 ||
      value.entityAffiliation.length > 0 ||
      value.gammaTeam.length > 0 ||
      value.tags.length > 0,
    [value],
  );

  return (
    <Stack
      alignItems={isMobile ? 'flex-start' : 'center'}
      gap="40px"
      width="100%"
      flexDirection={isMobile ? 'column' : 'row'}
    >
      <Stack width={isMobile ? '100%' : '390px'}>
        <Input placeholder="Search" value={search} onChange={onSearchChange} />
      </Stack>

      <Box
        display="grid"
        gridTemplateColumns={isMobile ? '300px' : '143px 143px 143px 143px 143px auto'}
        gap="16px"
      >
        <Select
          multiple
          showSelectAll
          placeholder="Status"
          options={statusOptions}
          value={status}
          onChange={onStatusChange}
        />

        <Select
          multiple
          showSelectAll
          placeholder="Entity Affiliation"
          options={booleanOptions}
          value={selectEntityAffiliationValue}
          onChange={onEntityAffiliationChange}
        />

        <Select
          multiple
          showSelectAll
          placeholder="Gamma Prime Team"
          options={booleanOptions}
          value={selectGammaTeamValue}
          onChange={onGammaTeamChange}
        />

        <Tooltip title={TAGS_DENIED_TOOLTIP} side="top" disabled={canViewTags}>
          <span>
            <Select
              multiple
              showSelectAll
              showSearch
              placeholder="Tags"
              ariaLabel="Tags"
              options={tagOptions}
              value={tags}
              onChange={onTagsChange}
              disabled={!canViewTags}
            />
          </span>
        </Tooltip>

        <Select
          multiple
          showSelectAll
          showSearch
          placeholder="Columns"
          options={columnsOptions}
          groups={columnsGroups}
          value={columns}
          onChange={onColumnsChange}
        />

        {showResetFilter && <ResetFiltersButton onClick={resetFilters} />}
      </Box>
    </Stack>
  );
};
